import { berlinUhrzeit, personImText, rollenbezeichnung, ROLLEN, type Anrede, type Rolle, type Rollenbezug } from "@dj/domain/gemeinsam";
import type { Zeile } from "../plattform/lesemodelle";

/** Darstellung von Kanälen und Nachrichten (lesemodelle.md §1.5, oberflaeche.md §2, §7). Nichts davon wird gespeichert. */

export function kanalname(k: Record<string, unknown>, mitDrueckjagd = true): string {
  return [k["Kanalart"], k["Gruppe"], k["Bezeichnung"], mitDrueckjagd ? k["Drückjagd"] : null].filter((x) => x !== null && x !== undefined && x !== "").join(" ");
}

export function uhrzeit(z: unknown): string {
  return z instanceof Date ? berlinUhrzeit(z) : "";
}

interface Personenangabe { Kennung?: string; Name?: string; Anrede?: string }

function istRolle(r: unknown): r is Rolle {
  return typeof r === "string" && (ROLLEN as readonly string[]).includes(r);
}

function bezugAus(z: Zeile): Rollenbezug {
  const b = (z["_bezug"] ?? {}) as Record<string, string>;
  return { ...(b["stand"] ? { stand: b["stand"] } : {}), ...(b["anstellgruppe"] ? { anstellgruppe: b["anstellgruppe"] } : {}),
    ...(b["treibergruppe"] ? { treibergruppe: b["treibergruppe"] } : {}) };
}

/**
 * Person im Fließtext (oberflaeche.md §2.1, §2.2): `<Rollenbezeichnung> <Name>`, ohne Anrede `<Name>, <Beschreibung>`.
 * Ohne bekannte Rolle steht nur der Name.
 */
export function personText(z: Zeile, feld: string): string {
  const p = z[feld] as Personenangabe | null | undefined;
  if (!p?.Name) return "";
  const rolle = z["Rolle"];
  if (!istRolle(rolle)) return p.Name;
  return personImText(p.Name, rolle, (p.Anrede ?? "ohne Anrede") as Anrede, bezugAus(z));
}

/** Die Rollenbezeichnung allein (zusätzliches Textelement am Verfasser, §2.1); ohne Anrede keine. */
export function bezeichnungAllein(z: Zeile, feld: string): string | null {
  const p = z[feld] as Personenangabe | null | undefined;
  const rolle = z["Rolle"];
  if (!p?.Name || !istRolle(rolle)) return null;
  return rollenbezeichnung(rolle, (p.Anrede ?? "ohne Anrede") as Anrede);
}

/** Felder einer Systemmeldung in der Reihenfolge der Anzeige (TESTSPRACHE §6). */
export const SYSTEMMELDUNG_FELDER: Record<string, readonly string[]> = {
  "Jagdzustand gewechselt": ["Jagdzustand", "Person"],
  "Jagdzeitplan geändert": ["Person"],
  "Kontingent gesperrt": ["Kontingent", "Kennzeichen"],
  "Freigabe gesperrt": ["Ansprache", "Kennzeichen"],
  Notruf: ["Notfallart", "Person", "Stand"],
  "Hund vermisst": ["Hund", "Beschreibung", "Person"],
  "Hund zurück": ["Hund", "Person"],
  Erlegung: ["Ansprache", "Person", "Stand"],
};

export const SYSTEMMELDUNG_SYMBOL: Record<string, string> = {
  "Jagdzustand gewechselt": "jagd-im-gange", "Jagdzeitplan geändert": "uhr", "Kontingent gesperrt": "sperre",
  "Freigabe gesperrt": "sperre", Notruf: "notruf", "Hund vermisst": "hund-vermisst", "Hund zurück": "hund-zurueck", Erlegung: "strecke",
};

export function anhangText(a: unknown): string | null {
  if (!a || typeof a !== "object") return null;
  const x = a as Record<string, unknown>;
  switch (x["Medienart"]) {
    case "Foto": return x["Auflösung"] ? `Foto, ${String(x["Auflösung"])}` : "Foto";
    case "Video": return x["Dauer"] ? `Video, ${String(x["Dauer"])} s` : "Video";
    case "Sprachnachricht": return x["Dauer"] ? `Sprachnachricht, ${String(x["Dauer"])} s` : "Sprachnachricht";
    case "Dokument": return "Dokument";
    case "Standort": return "Standort";
    default: return null;
  }
}
